import type { TicketEarnings, TicketSettings } from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { RefreshCw, Ticket } from 'lucide-react'
import { useCallback } from 'react'
import { useProfileScope } from '~/entities/profile/model/use-profiles'
import { formatMoney, formatPayoutDay, nextPayoutDate } from '~/entities/ticket/lib/ticket-earnings'
import { useTicketEarnings, useTicketSettings } from '~/entities/ticket/model/use-tickets'
import type { PayoutReturn } from '~/features/events/tickets/payout-return'
import {
  PayoutSetupButton,
  PayoutSetupPrompt,
  usePayoutReturn,
} from '~/features/events/tickets/payout-setup'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { MINUTES } from '~/shared/lib/time-constants'
import { useNow } from '~/shared/lib/use-now'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { Skeleton } from '~/shared/ui/skeleton'
import {
  type EarningsFilter,
  type EarningsSearch,
  type EarningsSort,
  earningsSearch,
  earningsView,
  filterBy,
  sortBy,
  visibleEarnings,
} from '../lib/earnings-list'
import { EarningsList } from './earnings-list'

const FIGURE_BORDERS = ['', 'border-l', 'border-t md:border-t-0 md:border-l', 'border-t border-l']
const FIGURE_SKELETONS = ['sold', 'gross', 'net', 'outstanding']

interface EarningsPageProps {
  search: EarningsSearch
  payoutReturn?: PayoutReturn
  onSearchChange: (search: EarningsSearch) => void
}

export function EarningsPage({ search, payoutReturn, onSearchChange }: EarningsPageProps) {
  const locale = getLocale()
  const now = new Date(useNow(MINUTES))
  const { businessId, scope, isLoading: scopeLoading } = useProfileScope()
  const hostProfileId = scope.kind === 'organization' ? undefined : scope.profileId
  const earnings = useTicketEarnings(businessId ?? '', hostProfileId, !scopeLoading)
  const settings = useTicketSettings(businessId ?? '', hostProfileId)
  const view = earningsView(search)
  const leaveReturn = useCallback(() => onSearchChange(search), [onSearchChange, search])
  usePayoutReturn(businessId ?? '', payoutReturn, leaveReturn, hostProfileId)

  if (earnings.isError && !earnings.data) {
    return (
      <Card className="flex flex-col items-start gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-destructive text-sm">{m.earningsLoadError()}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => earnings.refetch()}
          disabled={earnings.isFetching}
        >
          <RefreshCw className={cn(earnings.isFetching && 'motion-safe:animate-spin')} />
          {m.tryAgain()}
        </Button>
      </Card>
    )
  }

  if (!earnings.data) return <EarningsSkeleton />

  const data = earnings.data
  if (data.events.length === 0) return <EarningsEmpty />

  const sellsPaid = data.events.some((row) => row.sellsPaid)
  const rows = visibleEarnings(data.events, view, now)

  return (
    <div className="flex flex-col gap-6">
      {sellsPaid && businessId && settings.data ? (
        <PayoutHint
          businessId={businessId}
          hostProfileId={hostProfileId}
          settings={settings.data}
        />
      ) : null}
      <EarningsFigures data={data} locale={locale} now={now} />
      {data.profiles.length > 1 ? <EarningsByProfile data={data} locale={locale} /> : null}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl<EarningsFilter>
          label={m.earningsFilterLabel()}
          value={view.filter}
          options={[
            { value: 'upcoming', label: m.earningsFilterUpcoming() },
            { value: 'past', label: m.earningsFilterPast() },
            { value: 'all', label: m.earningsFilterAll() },
          ]}
          onChange={(filter) => onSearchChange(earningsSearch(filterBy(view, filter)))}
        />
        <SortControl
          sort={view.sort}
          ascending={view.direction === 'asc'}
          onChange={(sort) => onSearchChange(earningsSearch(sortBy(view, sort)))}
        />
      </div>
      {rows.length === 0 ? (
        <p className="rounded-xl border border-border border-dashed p-6 text-center text-muted-foreground text-sm">
          {m.earningsFilteredEmpty()}
        </p>
      ) : (
        <EarningsList rows={rows} currency={data.currency} locale={locale} now={now} />
      )}
      {sellsPaid && settings.data?.connect.payoutsEnabled ? (
        <p className="text-muted-foreground text-sm">{m.ticketsPayoutsReady()}</p>
      ) : null}
    </div>
  )
}

function EarningsByProfile({ data, locale }: { data: TicketEarnings; locale: string }) {
  const money = (cents: number) => formatMoney(cents, data.currency, locale)
  return (
    <Card className="overflow-hidden">
      <p className="border-border border-b px-5 py-2.5 font-medium text-muted-foreground text-xs">
        {m.earningsByProfile()}
      </p>
      <ul className="divide-y divide-border">
        {data.profiles.map((row) => (
          <li
            key={row.profileId ?? 'none'}
            className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-4 px-5 py-3 text-sm"
          >
            <span className="truncate font-medium">{row.name ?? m.earningsNoProfile()}</span>
            <span className="text-muted-foreground tabular-nums">
              {new Intl.NumberFormat(locale).format(row.ticketsSold)} {m.earningsSortSold()}
            </span>
            <span className="font-medium tabular-nums">{money(row.netCents)}</span>
          </li>
        ))}
      </ul>
    </Card>
  )
}

function SortControl({
  sort,
  ascending,
  onChange,
}: {
  sort: EarningsSort
  ascending: boolean
  onChange: (sort: EarningsSort) => void
}) {
  const arrow = ascending ? ' ↑' : ' ↓'
  const options = (
    [
      ['date', m.earningsSortDate()],
      ['gross', m.earningsSortGross()],
      ['sold', m.earningsSortSold()],
    ] as const
  ).map(([value, label]) => ({ value, label: value === sort ? `${label}${arrow}` : label }))
  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground text-xs">{m.earningsSortLabel()}</span>
      <SegmentedControl<EarningsSort>
        size="sm"
        label={`${m.earningsSortLabel()}, ${ascending ? m.earningsSortAscending() : m.earningsSortDescending()}`}
        value={sort}
        options={options}
        onChange={onChange}
      />
    </div>
  )
}

function EarningsFigures({
  data,
  locale,
  now,
}: {
  data: TicketEarnings
  locale: string
  now: Date
}) {
  const { totals } = data
  const money = (cents: number) => formatMoney(cents, data.currency, locale)
  const next = nextPayoutDate(data.events, now)
  const figures = [
    {
      key: 'sold',
      label: m.earningsSold(),
      value: new Intl.NumberFormat(locale).format(totals.ticketsSold),
      note: totals.freeTickets > 0 ? m.earningsFreeCount({ count: totals.freeTickets }) : null,
    },
    {
      key: 'gross',
      label: m.earningsGross(),
      value: money(totals.grossCents),
      note:
        totals.refundedCents > 0
          ? m.earningsRefunded({ amount: money(totals.refundedCents) })
          : null,
    },
    {
      key: 'net',
      label: m.earningsNet(),
      value: money(totals.netCents),
      note: totals.feeCents > 0 ? m.earningsAfterFee({ fee: money(totals.feeCents) }) : null,
    },
    {
      key: 'outstanding',
      label: m.earningsOutstanding(),
      value: money(totals.outstandingCents),
      note: next
        ? m.earningsNextPayout({ date: formatPayoutDay(next, locale) })
        : totals.paidOutCents > 0
          ? m.earningsPaidOutSoFar({ amount: money(totals.paidOutCents) })
          : null,
    },
  ]

  return (
    <Card className="grid grid-cols-2 md:grid-cols-4">
      {figures.map((figure, index) => (
        <div
          key={figure.key}
          className={cn('flex min-w-0 flex-col gap-2 p-4 sm:p-5', FIGURE_BORDERS[index])}
        >
          <p className="truncate font-medium text-muted-foreground text-xs uppercase tracking-wider">
            {figure.label}
          </p>
          <p className="truncate font-semibold font-serif text-2xl text-foreground tabular-nums leading-none tracking-tight sm:text-3xl">
            {figure.value}
          </p>
          {figure.note ? (
            <p className="text-muted-foreground text-xs leading-snug">{figure.note}</p>
          ) : null}
        </div>
      ))}
    </Card>
  )
}

function PayoutHint({
  businessId,
  hostProfileId,
  settings,
}: {
  businessId: string
  hostProfileId?: string
  settings: TicketSettings
}) {
  const { connect } = settings
  if (!connect.onboardingComplete) {
    return (
      <PayoutSetupPrompt
        businessId={businessId}
        hostProfileId={hostProfileId}
        settings={settings}
      />
    )
  }
  if (connect.payoutsEnabled) return null
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-muted px-3 py-2.5">
      <p className="min-w-0 flex-1 text-sm">{m.ticketsPayoutsChecking()}</p>
      <PayoutSetupButton
        businessId={businessId}
        hostProfileId={hostProfileId}
        started
        variant="outline"
      />
    </div>
  )
}

function EarningsEmpty() {
  return (
    <Card className="flex flex-col items-start gap-4 p-6 sm:p-8">
      <span className="flex size-10 items-center justify-center rounded-full bg-muted">
        <Ticket aria-hidden className="size-5 text-muted-foreground" />
      </span>
      <div className="flex max-w-prose flex-col gap-1.5">
        <h2 className="font-semibold font-serif text-foreground text-xl tracking-tight">
          {m.earningsEmptyTitle()}
        </h2>
        <p className="text-muted-foreground text-sm leading-6">{m.earningsEmptyBody()}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button asChild size="sm">
          <Link to="/dashboard/events">{m.earningsEmptyEvents()}</Link>
        </Button>
        <Button asChild size="sm" variant="outline">
          <Link to="/dashboard/events/create">{m.createEvent()}</Link>
        </Button>
      </div>
    </Card>
  )
}

function EarningsSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <Card className="grid grid-cols-2 md:grid-cols-4">
        {FIGURE_SKELETONS.map((key, index) => (
          <div key={key} className={cn('flex flex-col gap-2 p-4 sm:p-5', FIGURE_BORDERS[index])}>
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-8 w-28" />
          </div>
        ))}
      </Card>
      <Skeleton className="h-9 w-64 rounded-lg" />
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  )
}
